import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../layouts/AdminLayout';
import {
  Card, CardContent, Typography, Box, Grid, TextField, Button,
  Switch, FormControlLabel, Chip, makeStyles, CircularProgress,
  Snackbar, Divider, IconButton, Tooltip, InputAdornment, Paper
} from '@material-ui/core';
import SystemUpdateAltIcon from '@material-ui/icons/SystemUpdateAlt';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import SaveIcon from '@material-ui/icons/Save';
import RefreshIcon from '@material-ui/icons/Refresh';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import AndroidIcon from '@material-ui/icons/Android';
import PeopleIcon from '@material-ui/icons/People';
import WarningIcon from '@material-ui/icons/Warning';
import LockIcon from '@material-ui/icons/Lock';
import PhoneIphoneIcon from '@material-ui/icons/PhoneIphone';
import AutoAwesomeIcon from '@material-ui/icons/Stars';
import { useAuth } from '../../context/AuthContext';
import {
  getVersionSettings,
  saveVersionSettings,
  getAppVersionStats,
  DEFAULT_VERSION_SETTINGS
} from '../../services/versionService';

const useStyles = makeStyles((theme) => ({
  container: {
    maxWidth: 1200,
    margin: '0 auto',
  },
  header: {
    marginBottom: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  pageTitle: {
    fontWeight: 700,
    fontSize: '1.4rem',
    color: theme.palette.text.primary,
    fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif',
  },
  pageSubtitle: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    marginTop: 4,
  },
  card: {
    borderRadius: 20,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    boxShadow: 'none',
    overflow: 'hidden',
  },
  cardHeader: {
    padding: '20px 24px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  cardTitle: {
    fontWeight: 700,
    fontSize: '1.05rem',
    color: theme.palette.text.primary,
  },
  saveBtn: {
    borderRadius: 12,
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: '#fff',
    fontWeight: 700,
    padding: '10px 28px',
    boxShadow: '0 4px 14px rgba(16,185,129,0.35)',
    '&:hover': {
      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
    },
    '&:disabled': {
      background: 'rgba(148,163,184,0.3)',
      color: 'rgba(255,255,255,0.6)',
    },
  },
  presetChip: {
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '0.75rem',
    borderRadius: 8,
    transition: 'all 0.2s ease',
    '&:hover': {
      background: 'rgba(16,185,129,0.15)',
      borderColor: '#10b981',
    },
  },
  previewPhone: {
    width: '100%',
    maxWidth: 380,
    margin: '0 auto',
    borderRadius: 32,
    border: `8px solid ${theme.palette.type === 'dark' ? '#334155' : '#e2e8f0'}`,
    background: theme.palette.type === 'dark' ? '#0f172a' : '#f8fafc',
    boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 560,
  },
  previewScreen: {
    flex: 1,
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    background: theme.palette.type === 'dark' ? '#0f172a' : '#f1f5f9',
    position: 'relative',
  },
  phoneStatusBar: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '6px 16px 12px',
    fontSize: '0.7rem',
    fontWeight: 600,
    color: theme.palette.text.disabled,
  },
  bottomSheetMock: {
    borderRadius: '24px 24px 20px 20px',
    background: theme.palette.type === 'dark' ? '#1e293b' : '#ffffff',
    padding: '24px 20px 20px',
    boxShadow: '0 -8px 24px rgba(0,0,0,0.2)',
    border: `1px solid ${theme.palette.divider}`,
    textAlign: 'center',
    position: 'relative',
  },
  unclosableBadge: {
    position: 'absolute',
    top: -12,
    left: '50%',
    transform: 'translateX(-50%)',
    background: '#e11d48',
    color: '#fff',
    fontSize: '0.65rem',
    fontWeight: 700,
    padding: '3px 10px',
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    boxShadow: '0 2px 8px rgba(225,29,72,0.4)',
  },
  sheetIconWrapper: {
    width: 60,
    height: 60,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    marginBottom: 12,
    boxShadow: '0 6px 18px rgba(16,185,129,0.35)',
  },
  sheetTitle: {
    fontWeight: 800,
    fontSize: '1.05rem',
    color: theme.palette.text.primary,
    marginBottom: 4,
  },
  sheetDesc: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.45,
    marginBottom: 14,
  },
  whatsNewBox: {
    borderRadius: 14,
    background: 'rgba(16,185,129,0.06)',
    border: '1px solid rgba(16,185,129,0.18)',
    padding: 12,
    textAlign: 'left',
    marginBottom: 16,
  },
  whatsNewTitle: {
    fontSize: '0.75rem',
    fontWeight: 700,
    color: '#10b981',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginBottom: 8,
  },
  whatsNewItem: {
    fontSize: '0.72rem',
    color: theme.palette.text.primary,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 6,
    marginBottom: 4,
  },
  updateNowBtn: {
    width: '100%',
    borderRadius: 12,
    background: '#10b981',
    color: '#fff',
    fontWeight: 700,
    padding: '10px 0',
    fontSize: '0.85rem',
    textTransform: 'none',
    '&:hover': {
      background: '#059669',
    },
  },
  featureItemInput: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  statCard: {
    padding: 16,
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    textAlign: 'center',
  },
}));

export default function AppVersionPage() {
  const classes = useStyles();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);

  // Form State
  const [version, setVersion] = useState('1.1.1');
  const [forceUpdate, setForceUpdate] = useState(true);
  const [title, setTitle] = useState('নতুন সংস্করণ উপলব্ধ');
  const [message, setMessage] = useState(
    'Noor অ্যাপের একটি নতুন সংস্করণ উপলব্ধ আছে। অ্যাপের সকল ফিচার নির্বিঘ্নে উপভোগ করতে এবং সেরা অভিজ্ঞতা পেতে অনুগ্রহ করে এখনই আপডেট করে নিন।'
  );
  const [playStoreUrl, setPlayStoreUrl] = useState(
    'https://play.google.com/store/apps/details?id=com.rizkware.noor'
  );
  const [whatsNewList, setWhatsNewList] = useState([
    'নতুন ফিচার এবং উন্নত পারফরম্যান্স',
    'বাগ ফিক্স এবং অ্যাপের স্থিতিশীলতা বৃদ্ধি',
    'মসৃণ এবং দ্রুত ব্যবহারকারী অভিজ্ঞতা',
  ]);
  const [newFeatureText, setNewFeatureText] = useState('');

  // Stats State
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalTrackedUsers: 0,
    matchingUsers: 0,
    outdatedUsers: 0,
    versionBreakdown: [],
  });

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Load Settings
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getVersionSettings();
      setVersion(data.version || '1.1.1');
      setForceUpdate(data.forceUpdate !== undefined ? data.forceUpdate : true);
      setTitle(data.title || DEFAULT_VERSION_SETTINGS.title);
      setMessage(data.message || DEFAULT_VERSION_SETTINGS.message);
      setPlayStoreUrl(data.playStoreUrl || DEFAULT_VERSION_SETTINGS.playStoreUrl);
      setWhatsNewList(
        data.whatsNew && data.whatsNew.length > 0
          ? data.whatsNew
          : DEFAULT_VERSION_SETTINGS.whatsNew
      );

      // Fetch user version stats
      setStatsLoading(true);
      const versionStats = await getAppVersionStats(data.version || '1.1.1');
      setStats(versionStats);
      setStatsLoading(false);
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: 'Failed to load version settings: ' + err.message,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Recalculate stats when version changes
  const handleVersionChange = (newVal) => {
    setVersion(newVal);
    if (stats.versionBreakdown.length > 0) {
      const norm = newVal.toLowerCase().replace(/^v/, '').trim();
      let match = 0;
      let outdated = 0;
      stats.versionBreakdown.forEach((b) => {
        if (b.version === norm) {
          match += b.count;
        } else {
          outdated += b.count;
        }
      });
      setStats((prev) => ({
        ...prev,
        matchingUsers: match,
        outdatedUsers: outdated,
      }));
    }
  };

  // Add What's New Item
  const handleAddFeature = () => {
    if (!newFeatureText.trim()) return;
    setWhatsNewList([...whatsNewList, newFeatureText.trim()]);
    setNewFeatureText('');
  };

  // Remove What's New Item
  const handleRemoveFeature = (index) => {
    setWhatsNewList(whatsNewList.filter((_, i) => i !== index));
  };

  // Save Settings
  const handleSave = async () => {
    if (!version.trim()) {
      setSnackbar({ open: true, message: 'Version name cannot be empty', severity: 'error' });
      return;
    }

    setSaving(true);
    try {
      await saveVersionSettings(
        {
          version: version.trim(),
          forceUpdate,
          title: title.trim(),
          message: message.trim(),
          playStoreUrl: playStoreUrl.trim(),
          whatsNew: whatsNewList,
        },
        user?.email || 'Admin'
      );

      setSnackbar({
        open: true,
        message: `Version "${version.trim()}" saved and broadcast to all Noor mobile apps!`,
        severity: 'success',
      });

      // Refresh stats
      const versionStats = await getAppVersionStats(version.trim());
      setStats(versionStats);
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: 'Failed to save settings: ' + err.message,
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  // Quick preset helper
  const getNextVersion = (type) => {
    const parts = version.split('.').map(Number);
    if (parts.length >= 3 && !parts.some(isNaN)) {
      if (type === 'patch') {
        parts[2] += 1;
      } else if (type === 'minor') {
        parts[1] += 1;
        parts[2] = 0;
      }
      return parts.join('.');
    }
    return version + '.1';
  };

  if (loading) {
    return (
      <AdminLayout title="App Version & Update Control">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
          <CircularProgress style={{ color: '#10b981' }} />
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="App Version & Update Control" onRefresh={loadData}>
      <div className={classes.container}>
        {/* Header */}
        <div className={classes.header}>
          <div>
            <Typography className={classes.pageTitle}>App Version & Update Settings</Typography>
            <Typography className={classes.pageSubtitle}>
              Configure target version in Firebase Firestore. Any mobile app not matching this version will display the unclosable update bottom sheet.
            </Typography>
          </div>
          <Box display="flex" alignItems="center" style={{ gap: 16 }}>
            <Button
              variant="outlined"
              onClick={loadData}
              startIcon={<RefreshIcon />}
              style={{ borderRadius: 12, textTransform: 'none' }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              className={classes.saveBtn}
              onClick={handleSave}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            >
              {saving ? 'Saving...' : 'Save & Publish'}
            </Button>
          </Box>
        </div>

        {/* User Impact Summary Cards */}
        <Grid container spacing={3} style={{ marginBottom: 24 }}>
          <Grid item xs={12} sm={4}>
            <Paper className={classes.statCard} elevation={0}>
              <Box display="flex" alignItems="center" justifyContent="center" gap={1} mb={0.5}>
                <AndroidIcon style={{ color: '#10b981', fontSize: 20 }} />
                <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
                  CURRENT ACTIVE VERSION
                </Typography>
              </Box>
              <Typography variant="h5" style={{ fontWeight: 800, color: '#10b981' }}>
                v{version}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Set in Firebase Firestore
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper className={classes.statCard} elevation={0}>
              <Box display="flex" alignItems="center" justifyContent="center" gap={1} mb={0.5}>
                <CheckCircleIcon style={{ color: '#059669', fontSize: 20 }} />
                <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
                  USERS MATCHING VERSION
                </Typography>
              </Box>
              <Typography variant="h5" style={{ fontWeight: 800, color: '#059669' }}>
                {statsLoading ? '...' : stats.matchingUsers}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                App version identical (No alert)
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper className={classes.statCard} elevation={0}>
              <Box display="flex" alignItems="center" justifyContent="center" gap={1} mb={0.5}>
                <WarningIcon style={{ color: '#e11d48', fontSize: 20 }} />
                <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
                  USERS WILL BE FORCED TO UPDATE
                </Typography>
              </Box>
              <Typography variant="h5" style={{ fontWeight: 800, color: '#e11d48' }}>
                {statsLoading ? '...' : stats.outdatedUsers}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Will see unclosable update bottom sheet
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Grid container spacing={4}>
          {/* Left Column: Form Settings */}
          <Grid item xs={12} md={7}>
            <Card className={classes.card} elevation={0}>
              <div className={classes.cardHeader}>
                <SystemUpdateAltIcon style={{ color: '#10b981' }} />
                <div>
                  <Typography className={classes.cardTitle}>Version Configuration</Typography>
                  <Typography variant="caption" color="textSecondary">
                    Set the exact version string to match against mobile apps
                  </Typography>
                </div>
              </div>

              <CardContent style={{ padding: 24 }}>
                {/* Version Name */}
                <Box mb={3}>
                  <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8 }}>
                    Target Version Name (সংস্করণ নাম) *
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={version}
                    onChange={(e) => handleVersionChange(e.target.value)}
                    placeholder="e.g. 1.1.1, 1.1.2, 1.2.0"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Chip label="v" size="small" style={{ fontWeight: 700 }} />
                        </InputAdornment>
                      ),
                      style: { fontSize: '1.1rem', fontWeight: 700 },
                    }}
                    helperText="Whichever version name you enter here will be checked by the app. If the app version differs, users will see the unclosable update bottom sheet."
                  />

                  {/* Quick Presets */}
                  <Box mt={1.5} display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 8 }}>
                    <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
                      Quick Suggestions:
                    </Typography>
                    <Chip
                      size="small"
                      variant="outlined"
                      label="Current: 1.1.1"
                      className={classes.presetChip}
                      onClick={() => handleVersionChange('1.1.1')}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Next Patch: ${getNextVersion('patch')}`}
                      className={classes.presetChip}
                      onClick={() => handleVersionChange(getNextVersion('patch'))}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Next Minor: ${getNextVersion('minor')}`}
                      className={classes.presetChip}
                      onClick={() => handleVersionChange(getNextVersion('minor'))}
                    />
                    {stats.versionBreakdown.slice(0, 3).map((b) => (
                      <Chip
                        key={b.version}
                        size="small"
                        label={`v${b.version} (${b.count} users)`}
                        className={classes.presetChip}
                        onClick={() => handleVersionChange(b.version)}
                      />
                    ))}
                  </Box>
                </Box>

                {/* Force Update Switch */}
                <Box mb={3} p={2} style={{ borderRadius: 14, background: 'rgba(225,29,72,0.05)', border: '1px solid rgba(225,29,72,0.18)' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={forceUpdate}
                        onChange={(e) => setForceUpdate(e.target.checked)}
                        color="secondary"
                      />
                    }
                    label={
                      <Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          <LockIcon style={{ fontSize: 18, color: '#e11d48' }} />
                          <Typography variant="subtitle2" style={{ fontWeight: 700, color: '#e11d48' }}>
                            Unclosable Force Update (বাধ্যতামূলক আপডেট)
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="textSecondary">
                          When active, the bottom sheet cannot be dismissed by tapping outside, dragging down, or pressing the back button. The user MUST update.
                        </Typography>
                      </Box>
                    }
                  />
                </Box>

                {/* Update Title */}
                <Box mb={3}>
                  <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8 }}>
                    Bottom Sheet Title (শিরোনাম)
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g. নতুন সংস্করণ উপলব্ধ / Update Required"
                  />
                </Box>

                {/* Update Message */}
                <Box mb={3}>
                  <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8 }}>
                    Update Description (বার্তা / বিবরণ)
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    variant="outlined"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Explain why the update is required..."
                  />
                </Box>

                {/* Play Store URL */}
                <Box mb={3}>
                  <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8 }}>
                    Google Play Store / Download URL
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={playStoreUrl}
                    onChange={(e) => setPlayStoreUrl(e.target.value)}
                    placeholder="https://play.google.com/store/apps/details?id=com.rizkware.noor"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title="Test Link">
                            <IconButton
                              size="small"
                              href={playStoreUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                {/* What's New Bullet Points */}
                <Box mb={2}>
                  <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8 }}>
                    What's New Highlights (নতুন কী কী আছে)
                  </Typography>
                  {whatsNewList.map((item, idx) => (
                    <div key={idx} className={classes.featureItemInput}>
                      <TextField
                        fullWidth
                        size="small"
                        variant="outlined"
                        value={item}
                        onChange={(e) => {
                          const updated = [...whatsNewList];
                          updated[idx] = e.target.value;
                          setWhatsNewList(updated);
                        }}
                      />
                      <IconButton size="small" onClick={() => handleRemoveFeature(idx)} style={{ color: '#e11d48' }}>
                        <RemoveCircleOutlineIcon fontSize="small" />
                      </IconButton>
                    </div>
                  ))}

                  <Box display="flex" mt={1} style={{ gap: 8 }}>
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      placeholder="Add another highlight..."
                      value={newFeatureText}
                      onChange={(e) => setNewFeatureText(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddFeature()}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddCircleOutlineIcon />}
                      onClick={handleAddFeature}
                      style={{ whiteSpace: 'nowrap', borderRadius: 8 }}
                    >
                      Add
                    </Button>
                  </Box>
                </Box>

                <Box mt={4} pt={2} borderTop={`1px solid ${classes.divider || '#eee'}`}>
                  <Button
                    variant="contained"
                    fullWidth
                    className={classes.saveBtn}
                    onClick={handleSave}
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                  >
                    {saving ? 'Publishing to Firebase...' : 'Save & Publish Version to Firebase'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Column: Live Mobile BottomSheet Preview */}
          <Grid item xs={12} md={5}>
            <Box mb={2} display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center" gap={1}>
                <PhoneIphoneIcon style={{ color: '#10b981' }} />
                <Typography className={classes.cardTitle}>Mobile BottomSheet Preview</Typography>
              </Box>
              <Chip size="small" label="Live View" style={{ background: 'rgba(16,185,129,0.1)', color: '#10b981', fontWeight: 700 }} />
            </Box>

            <div className={classes.previewPhone}>
              {/* Phone Status Bar */}
              <div className={classes.phoneStatusBar}>
                <span>9:41</span>
                <span>LTE • 100%</span>
              </div>

              {/* Background Mock View */}
              <div className={classes.previewScreen}>
                <div style={{ textAlign: 'center', opacity: 0.25, marginBottom: 60 }}>
                  <Typography variant="h6" style={{ fontWeight: 800 }}>Noor App</Typography>
                  <Typography variant="caption">Home / Dashboard Screen</Typography>
                </div>

                {/* Simulated Bottom Sheet */}
                <div className={classes.bottomSheetMock}>
                  {forceUpdate && (
                    <div className={classes.unclosableBadge}>
                      <LockIcon style={{ fontSize: 11 }} />
                      UNCLOSABLE ALERT
                    </div>
                  )}

                  {/* Icon */}
                  <div className={classes.sheetIconWrapper}>
                    <SystemUpdateAltIcon style={{ fontSize: 32 }} />
                  </div>

                  {/* Title */}
                  <Typography className={classes.sheetTitle}>
                    {title || 'Update Available'}
                  </Typography>

                  {/* Version Compare Badge */}
                  <Box display="flex" justifyContent="center" alignItems="center" gap={1} mb={1.5}>
                    <Chip
                      size="small"
                      label="Current: v1.1.0"
                      style={{ fontSize: '0.68rem', height: 22, background: 'rgba(148,163,184,0.15)' }}
                    />
                    <Typography variant="caption" style={{ fontWeight: 800, color: '#10b981' }}>
                      ➔
                    </Typography>
                    <Chip
                      size="small"
                      label={`Required: v${version || '1.1.1'}`}
                      style={{
                        fontSize: '0.68rem',
                        height: 22,
                        background: 'rgba(16,185,129,0.15)',
                        color: '#059669',
                        fontWeight: 700,
                      }}
                    />
                  </Box>

                  {/* Subtitle / Message */}
                  <Typography className={classes.sheetDesc}>
                    {message || 'A new update is available. Please update now to continue.'}
                  </Typography>

                  {/* What's New Box */}
                  {whatsNewList.length > 0 && (
                    <div className={classes.whatsNewBox}>
                      <div className={classes.whatsNewTitle}>
                        <AutoAwesomeIcon style={{ fontSize: 13 }} />
                        <span>নতুন কী আছে (What's New)</span>
                      </div>
                      {whatsNewList.map((item, i) => (
                        <div key={i} className={classes.whatsNewItem}>
                          <CheckCircleIcon style={{ fontSize: 12, color: '#10b981', marginTop: 2, flexShrink: 0 }} />
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Update Now Action Button */}
                  <Button variant="contained" className={classes.updateNowBtn}>
                    এখনই আপডেট করুন (Update Now)
                  </Button>

                  {/* Note about unclosable */}
                  <Box mt={1.5}>
                    <Typography variant="caption" style={{ fontSize: '0.68rem', color: '#94a3b8' }}>
                      {forceUpdate
                        ? '🔒 Tap outside and back gestures are disabled until updated'
                        : 'Tap outside to dismiss'}
                    </Typography>
                  </Box>
                </div>
              </div>
            </div>
          </Grid>
        </Grid>

        <Snackbar
          open={snackbar.open}
          message={snackbar.message}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
      </div>
    </AdminLayout>
  );
}
